/**
 * Radial geometry — the arc maths behind pie and donut.
 *
 * Split from geometry.js along the same seam as the plot components: a pie
 * shares no scale, no axis and no bands with a bar chart, only the surrounding
 * block.
 *
 * Coordinates are in the 0–100 square viewBox the radial plot draws into, which
 * the stylesheet keeps square so the HTML labels can sit at the same
 * percentages as the slices.
 *
 * Mirrors Chart\RadialGeometry in PHP. Change both together.
 */

import { clamp, toNumber } from './geometry';

/**
 * Slice geometry for a pie or donut.
 *
 * Negative values cannot be a share of a whole, so they are dropped rather than
 * silently flipped into a wedge that misstates the data.
 *
 * @param {number[]} data      Values.
 * @param {number}   innerRatio Inner radius as a fraction of the outer (0 = pie).
 * @return {{slices: Array, total: number}} Slice geometry.
 */
export function pieSlices( data, innerRatio = 0 ) {
	const values = ( data || [] ).map( ( value ) =>
		Math.max( 0, toNumber( value ) )
	);
	const total = values.reduce( ( sum, value ) => sum + value, 0 );
	const cx = 50;
	const cy = 50;
	const outer = 48;
	const inner = outer * clamp( innerRatio, 0, 0.9 );

	if ( ! total ) {
		return { slices: [], total: 0 };
	}

	let angle = -Math.PI / 2; // 12 o'clock.
	const slices = values.map( ( value, index ) => {
		const share = value / total;
		const start = angle;
		const end = angle + share * Math.PI * 2;
		angle = end;
		return {
			index,
			value,
			share,
			d: arcPath( cx, cy, outer, inner, start, end ),
			// Mid-angle point on the label ring, for the leader-free direct label.
			labelX:
				cx +
				Math.cos( ( start + end ) / 2 ) * ( ( outer + inner ) / 2 ),
			labelY:
				cy +
				Math.sin( ( start + end ) / 2 ) * ( ( outer + inner ) / 2 ),
		};
	} );

	return { slices, total };
}

/**
 * Path for one annular (or full) sector.
 *
 * A single slice covering the whole circle would collapse — start and end land
 * on the same point — so it is drawn as two half arcs.
 *
 * @param {number} cx    Centre x.
 * @param {number} cy    Centre y.
 * @param {number} outer Outer radius.
 * @param {number} inner Inner radius (0 for a pie).
 * @param {number} start Start angle, radians.
 * @param {number} end   End angle, radians.
 * @return {string} SVG path data.
 */
function arcPath( cx, cy, outer, inner, start, end ) {
	const full = end - start >= Math.PI * 2 - 1e-9;
	if ( full ) {
		const half = start + Math.PI;
		const outerRing =
			`M ${ fmt( cx + outer * Math.cos( start ) ) } ${ fmt(
				cy + outer * Math.sin( start )
			) }` +
			` A ${ outer } ${ outer } 0 1 1 ${ fmt(
				cx + outer * Math.cos( half )
			) } ${ fmt( cy + outer * Math.sin( half ) ) }` +
			` A ${ outer } ${ outer } 0 1 1 ${ fmt(
				cx + outer * Math.cos( start )
			) } ${ fmt( cy + outer * Math.sin( start ) ) } Z`;
		if ( ! inner ) {
			return outerRing;
		}
		return (
			outerRing +
			` M ${ fmt( cx + inner * Math.cos( start ) ) } ${ fmt(
				cy + inner * Math.sin( start )
			) }` +
			` A ${ inner } ${ inner } 0 1 0 ${ fmt(
				cx + inner * Math.cos( half )
			) } ${ fmt( cy + inner * Math.sin( half ) ) }` +
			` A ${ inner } ${ inner } 0 1 0 ${ fmt(
				cx + inner * Math.cos( start )
			) } ${ fmt( cy + inner * Math.sin( start ) ) } Z`
		);
	}

	const large = end - start > Math.PI ? 1 : 0;
	const x0 = cx + outer * Math.cos( start );
	const y0 = cy + outer * Math.sin( start );
	const x1 = cx + outer * Math.cos( end );
	const y1 = cy + outer * Math.sin( end );

	if ( ! inner ) {
		return `M ${ fmt( cx ) } ${ fmt( cy ) } L ${ fmt( x0 ) } ${ fmt(
			y0
		) } A ${ outer } ${ outer } 0 ${ large } 1 ${ fmt( x1 ) } ${ fmt(
			y1
		) } Z`;
	}

	const x2 = cx + inner * Math.cos( end );
	const y2 = cy + inner * Math.sin( end );
	const x3 = cx + inner * Math.cos( start );
	const y3 = cy + inner * Math.sin( start );

	return (
		`M ${ fmt( x0 ) } ${ fmt( y0 ) }` +
		` A ${ outer } ${ outer } 0 ${ large } 1 ${ fmt( x1 ) } ${ fmt( y1 ) }` +
		` L ${ fmt( x2 ) } ${ fmt( y2 ) }` +
		` A ${ inner } ${ inner } 0 ${ large } 0 ${ fmt( x3 ) } ${ fmt( y3 ) }` +
		' Z'
	);
}


/**
 * Trim path coordinates to three decimals — beyond that is bytes, not precision.
 *
 * @param {number} value Coordinate.
 * @return {number} Trimmed coordinate.
 */
function fmt( value ) {
	return Math.round( value * 1000 ) / 1000;
}
